import React, { useState } from 'react';
import { signing } from './api';

/**
 * "Send for signature" modal.
 *
 * Sends an existing file to a client for e-signature via Documenso. The returned
 * signing link is ALWAYS the fallback delivery method, so on success we surface it
 * prominently as copyable text rather than auto-closing.
 *
 * Note: confirm_real_sms is deliberately NOT exposed here. Real-number SMS is gated
 * server-side by the allowlist; the UI only ever requests send_sms.
 */
export default function SignForSignatureModal({ file, clientId, darkMode, onClose, onSent }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const border = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const inputBg = darkMode ? '#1a1a1a' : '#fff';
  const subtleBg = darkMode ? '#1a1a1a' : '#f5f7fa';

  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerPhone, setSignerPhone] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);   // { signing_url, status, sms, ... }
  const [checkState, setCheckState] = useState(null);    // 5.40.3: "Check now" result
  const [copied, setCopied] = useState(false);

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 6, marginBottom: 12,
    background: inputBg, color: textPrimary, border: `1px solid ${border}`,
    fontSize: 13, boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, color: textSecondary, marginBottom: 4, display: 'block' };

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(signerEmail.trim());
  const canSubmit = signerName.trim() && emailValid && (!sendSms || signerPhone.trim());

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const res = await signing.send({
        client_id: clientId,
        file_id: file.id,
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim(),
        signer_phone: signerPhone.trim() || null,
        send_sms: sendSms,
      });
      setResult(res.data);
      if (onSent) onSent(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to create signature request.');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.signing_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: select the field so the user can Ctrl+C manually.
      const el = document.getElementById('signing-link-field');
      if (el) { el.focus(); el.select(); }
    }
  };

  const smsNote = (() => {
    if (!result?.sms) return null;
    const s = result.sms.status;
    const map = {
      not_requested: 'SMS not requested.',
      simulated: `SMS simulated (test number ${result.sms.to}) — not transmitted.`,
      blocked_no_confirm: 'SMS not sent — real-number sending is disabled.',
      blocked_not_allowlisted: 'SMS not sent — recipient not enabled for live SMS.',
      no_phone: 'SMS not sent — no phone number.',
      sent: `SMS sent to ${result.sms.to}.`,
      error: result.sms.opted_out ? `${result.sms.error}. Send the link another way.`   // 5.40.0: STOP opt-out
        : `SMS failed: ${result.sms.error || 'unknown error'}. Send the link manually.`,
      failed: 'SMS failed: the carrier refused it. Send the link manually.',
    };
    return map[s] || `SMS: ${s}`;
  })();

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: panelBg, color: textPrimary, border: `1px solid ${border}`, borderRadius: 10, padding: 24, width: 460, maxWidth: '95vw', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>✍️ Send for signature</h3>
          <span onClick={onClose} style={{ cursor: 'pointer', color: textSecondary, fontSize: 18 }}>✕</span>
        </div>

        <div style={{ fontSize: 12, color: textSecondary, marginBottom: 16, background: subtleBg, padding: '8px 10px', borderRadius: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          📄 {file.filename}
        </div>

        {!result ? (
          <>
            <label style={labelStyle}>Signer name *</label>
            <input style={inputStyle} value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Jane Doe" autoFocus />

            <label style={labelStyle}>Signer email *</label>
            <input style={inputStyle} value={signerEmail} onChange={e => setSignerEmail(e.target.value)} placeholder="jane@example.com" />

            <label style={labelStyle}>Signer phone {sendSms ? '*' : '(optional)'}</label>
            <input style={inputStyle} value={signerPhone} onChange={e => setSignerPhone(e.target.value)} placeholder="+1 281 555 0123" />

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: textPrimary, margin: '4px 0 16px', cursor: 'pointer' }}>
              <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)} />
              Text the signing link to the signer
            </label>

            {error && <div style={{ color: '#e07070', fontSize: 12, marginBottom: 10 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, background: 'transparent', color: textSecondary, border: `1px solid ${border}`, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={submit} disabled={!canSubmit || busy}
                style={{ padding: '8px 16px', borderRadius: 6, background: (!canSubmit || busy) ? '#2a4a60' : '#1d628a', color: (!canSubmit || busy) ? '#88a' : '#fff', border: 'none', cursor: (!canSubmit || busy) ? 'default' : 'pointer', fontSize: 13 }}>
                {busy ? 'Creating…' : 'Create & get link'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: '#3fa45b', marginBottom: 10 }}>
              ✓ Signature request created.
            </div>
            <label style={labelStyle}>Signing link (always works — copy &amp; send manually if needed)</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input id="signing-link-field" readOnly value={result.signing_url}
                onFocus={e => e.target.select()}
                style={{ ...inputStyle, marginBottom: 0, fontFamily: 'monospace', fontSize: 12 }} />
              <button onClick={copyLink}
                style={{ padding: '0 14px', borderRadius: 6, background: copied ? '#3fa45b' : '#1d628a', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            {smsNote && <div style={{ fontSize: 12, color: textSecondary, marginBottom: 16 }}>📱 {smsNote}</div>}
            {result.signed_document_id && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 12 }}>
                <button data-testid="sign-modal-check" disabled={checkState?.busy}
                  onClick={async () => {
                    setCheckState({ busy: true });
                    try {
                      const r = await signing.check(result.signed_document_id);
                      setCheckState({ ok: r.data.result === 'signed', text: r.data.message || r.data.result });
                    } catch (e) {
                      setCheckState({ ok: false, text: e.response?.data?.detail || "Couldn't reach Documenso — try again in a minute" });
                    }
                  }}
                  style={{ padding: '4px 10px', borderRadius: 6, background: 'transparent', color: '#4a9eda', border: '1px solid #4a9eda', cursor: 'pointer', fontSize: 12 }}>
                  {checkState?.busy ? 'Checking…' : 'Check now'}
                </button>
                {checkState && !checkState.busy && (
                  <span role="status" data-testid="sign-modal-check-result" style={{ color: checkState.ok ? '#3fa45b' : textSecondary }}>
                    {checkState.ok ? '✓ ' : ''}{checkState.text}
                  </span>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, background: '#1d628a', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13 }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
